import { useI18n } from '@/libraries/contexts/i18n-context';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import FilterChipList, {
  FilterChip,
  FilterChipListProps,
} from '@/components/organisms/filter-chip-list/filter-chip-list';
import { ModalProps } from '@/components/utilities/modal/modal';
import SlideIn from '@/components/utilities/slide-in/slide-in';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import FilterSlideInInputGroup, {
  Filter,
} from './filter-slide-in-input-group/filter-slide-in-input-group';
import styles from './filter-slide-in.module.scss';

export interface FilterSlideInProps extends Omit<ModalProps, 'children'> {
  /** The active filter tags to show */
  activeFilters?: FilterChipListProps<FilterChip>;
  /** The filters to show */
  filters: Filter[];
  /** The headline of the filter slide in */
  headline: string;
  /** An event that is triggered on click clear button */
  onClickClear?: () => void;
  /** An event that is triggered on click show results button */
  onClickViewResults?: () => void;
  /** The number of results */
  resultCount?: number;
  /** Target id where to scroll on open */
  scrollTargetId?: string;
}

const FilterSlideIn = ({
  activeFilters,
  filters,
  headline,
  onClickClear,
  onClickViewResults,
  resultCount,
  scrollTargetId,
  isOpen,
  setIsOpen,
  ...rest
}: FilterSlideInProps) => {
  const { t } = useI18n();
  const isTablet = useWindowSize('tablet');

  return (
    <SlideIn
      {...rest}
      className={styles.component}
      isOpen={isOpen}
      scrollTargetId={scrollTargetId}
      setIsOpen={setIsOpen}
    >
      <button className={styles.close} onClick={() => setIsOpen?.(false)}>
        <Icon name="Close" />
      </button>

      <Typography className={styles.label} tagStyle="labelLarge">
        {headline}
      </Typography>

      {activeFilters && (
        <FilterChipList
          {...activeFilters}
          className={styles.filterChipList}
          clearLabel={t('items.search.filters.clearAllFilters')}
        />
      )}

      {/* !NOTE - Added isOpen since FilterSlideIn is cutting off the scroll once we hit SearchResults page from homepage.
      motion.div inside the FilterSlideInInputGroup component is interfering with scroll*/}
      {isOpen &&
        filters.map(filter => (
          <FilterSlideInInputGroup
            key={filter.id}
            filter={filter}
            isTablet={isTablet}
          />
        ))}

      <div className={styles.buttonGroup}>
        <Button
          onClick={onClickClear}
          text={t('items.search.filterSlideIn.reset')}
          variant="secondary"
        />

        <Button
          className={styles.viewResults}
          onClick={onClickViewResults}
          text={t('items.search.filterSlideIn.viewResults', {
            number: resultCount,
          })}
          variant="primary"
        />
      </div>
    </SlideIn>
  );
};

FilterSlideIn.displayName = 'FilterSlideIn';

export default FilterSlideIn;
